import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable from "./components/SelectableEnrollmentTable";

interface BulkStudentPhotosModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
}

const BulkStudentPhotosModal: React.FC<BulkStudentPhotosModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  const handleClose = () => {
    setError("");
    onHide();
  };

  const handleDownload = async () => {
    setError("");
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setDownloading(true);
    try {
      const resp = await EnrollmentService.bulkStudentPhotos({
        enrollmentIds: selectedEnrollmentIds,
      });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "student_photos.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      handleClose();
    } catch (err: any) {
      // Error responses come back as a Blob when responseType is "blob".
      let message = "Failed to download photos";
      try {
        const text = await err?.response?.data?.text?.();
        if (text) {
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        }
      } catch {
        /* keep default message */
      }
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Download Student Photos (Bulk)</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ fontSize: 13, color: "var(--text-body)", marginBottom: 10 }}>
          Downloads a ZIP containing each selected student's photo on file. Students without a
          photo are skipped.
        </div>

        <SelectableEnrollmentTable
          enrollments={enrollments}
          selectedIds={selectedIds}
          allSelected={allSelected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          disabled={downloading}
          caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={downloading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={downloading || selectedEnrollmentIds.length === 0}
        >
          {downloading ? (
            <>
              <i className="fa fa-spinner fa-spin" /> Preparing…
            </>
          ) : (
            <>Download ZIP ({selectedEnrollmentIds.length})</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkStudentPhotosModal;
